import { createContext, useContext, useEffect, useState } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import { useAuth } from 'context/AuthContext';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { _localGet, _localSet } from 'utils/helpers/gimHelper';
import type { LeadContextValue, LeadProviderProps, ReportDateFilterState } from '../types';

const LeadContext = createContext<LeadContextValue | null>(null);
export const useLead = (): LeadContextValue => useContext(LeadContext) as LeadContextValue;

const createOffDateFilter = (): ReportDateFilterState => ({
    period: 'off',
    dates: null,
    customRange: null,
});

export const defLeadsStatus = [
    vconst.leadNew,
    vconst.leadInWoork,
    vconst.leadNeedOffer,
    vconst.leadNeedsAttention,
    vconst.leadOffer,
];

export const defLeadsDateFilter = createOffDateFilter();

const parseLeadsDateFilter = (raw: unknown): ReportDateFilterState => {
    const saved = raw as Partial<ReportDateFilterState> | null;
    if (
        !saved?.period ||
        saved.period === 'off' ||
        (saved.period !== 'month' &&
            saved.period !== 'quarter' &&
            saved.period !== 'year' &&
            saved.period !== 'custom')
    ) {
        return createOffDateFilter();
    }
    if (!saved.dates?.[0] || !saved.dates?.[1]) return createOffDateFilter();
    return {
        period: saved.period,
        dates: [saved.dates[0], saved.dates[1]],
        customRange:
            saved.customRange?.[0] && saved.customRange?.[1]
                ? [saved.customRange[0], saved.customRange[1]]
                : null,
    };
};

export const LeadProvider = ({ children }: LeadProviderProps) => {
    const { auth } = useAuth();
    const filterName = `voltox-${auth.user.ID}-leads-filter`;
    const orderName = `voltox-${auth.user.ID}-leads-order`;
    const descName = `voltox-${auth.user.ID}-leads-desc`;
    const dateFilterName = `voltox-${auth.user.ID}-leads-date-filter`;
    const dashboardName = `voltox-${auth.user.ID}-leads-dashboard`;

    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [leadsOrder, setLeadsOrder] = useState<string>(safeJSONParse(sessionStorage.getItem(orderName)) || '');
    const [leadsDesc, setLeadsDesc] = useState<boolean>(safeJSONParse(sessionStorage.getItem(descName)) === true);
    const [leadsCheck, setLeadsCheck] = useState<number[]>([]);
    const [leadsSatus, setLeadStatus] = useState<number[]>(
        safeJSONParse(sessionStorage.getItem(filterName)) || defLeadsStatus,
    );
    const [leadsDateFilter, setLeadsDateFilter] = useState<ReportDateFilterState>(() =>
        parseLeadsDateFilter(safeJSONParse(sessionStorage.getItem(dateFilterName))),
    );
    const [showDashboard, setShowDashboard] = useState<boolean>(() => {
        const raw = _localGet(dashboardName);
        if (raw === null || typeof raw === 'undefined') return true;
        return raw === '1';
    });

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    useEffect(() => {
        sessionStorage.setItem(filterName, JSON.stringify(leadsSatus));
    }, [leadsSatus, filterName]);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(leadsOrder));
    }, [leadsOrder, orderName]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(leadsDesc));
    }, [leadsDesc, descName]);

    useEffect(() => {
        sessionStorage.setItem(dateFilterName, JSON.stringify(leadsDateFilter));
    }, [leadsDateFilter, dateFilterName]);

    useEffect(() => {
        _localSet(dashboardName, showDashboard ? '1' : '0');
    }, [showDashboard, dashboardName]);

    const values: LeadContextValue = {
        showSpinner,
        setShowSpinner,
        leadsOrder,
        setLeadsOrder,
        leadsDesc,
        setLeadsDesc,
        leadsCheck,
        setLeadsCheck,
        leadsSatus,
        setLeadStatus,
        leadsDateFilter,
        setLeadsDateFilter,
        showDashboard,
        setShowDashboard,
        listResetNonce,
        triggerListReset,
        listRefreshNonce,
        triggerListRefresh,
    };

    return <LeadContext.Provider value={values}>{children}</LeadContext.Provider>;
};
